import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, Tag, Typography, Space, Rate, Tooltip } from 'antd';
import { ShoppingCartOutlined, ShopOutlined, EnvironmentOutlined } from '@ant-design/icons';
import FavoriteButton from '../favorites/FavoriteButton';
import FoundingPartnerBadge from '../badges/FoundingPartnerBadge';
import { getCityByLanguage } from '../../utils/locationFormat';
import './ProductCard.scss';

const { Text, Title } = Typography;

const ProductCard = ({ product }) => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  // Get price from API response (min_price, max_price are provided by backend)
  const getPriceRange = () => {
    const min = product.min_price ? parseFloat(product.min_price) : null;
    const max = product.max_price ? parseFloat(product.max_price) : null;

    if (min === null && max === null) {
      return { min: null, max: null, available: false };
    }

    return { min, max, available: true };
  };

  const priceRange = getPriceRange();
  const title = product.title || 'Product';
  const variantCount = product.variant_count || 0;
  const inStock = variantCount > 0; // If there are variants, assume it's in stock

  // Get image URL based on available sizes
  const getImageUrl = () => {
    if (!product.main_image) return null;

    // VersatileImageField returns an object with sizes
    if (typeof product.main_image === 'object') {
      return product.main_image.medium || product.main_image.large || product.main_image.full_size;
    }

    // Fallback for old format (direct URL string)
    return product.main_image;
  };

  const imageUrl = getImageUrl();

  return (
    <Card
      className="product-card"
      hoverable
      onClick={() => navigate(`/products/${product.id}`)}
      cover={
        <div className="product-image-container">
          {imageUrl ? (
            <img alt={title} src={imageUrl} />
          ) : (
            <div className="product-image-placeholder">
              <ShoppingCartOutlined />
            </div>
          )}
          {!inStock && (
            <div className="out-of-stock-overlay">
              <Tag color="red">Out of Stock</Tag>
            </div>
          )}
          {/* Partner logo overlay */}
          {product.partner_logo && (
            <div className="partner-logo-overlay">
              <img src={product.partner_logo} alt={product.partner_name} className="partner-logo-small" />
            </div>
          )}
          <div className="favorite-button-overlay">
            <FavoriteButton
              type="product"
              itemId={product.id}
              size="small"
            />
          </div>
        </div>
      }
    >
      <div className="product-card-content">
        <Title level={4} ellipsis={{ rows: 2 }} className="product-title">
          {title}
        </Title>

        {product.category && (
          <Tag color="purple" className="category-tag">
            {product.category.name}
          </Tag>
        )}

        {product.average_rating > 0 && (
          <div className="product-rating">
            <Rate disabled allowHalf value={parseFloat(product.average_rating)} style={{ fontSize: 14 }} />
            {product.rating_count > 0 && (
              <Text type="secondary" style={{ marginLeft: 8, fontSize: '0.85rem' }}>
                ({product.rating_count})
              </Text>
            )}
          </div>
        )}

        {priceRange.available ? (
          <div className="product-price">
            <Text type="secondary" style={{ fontSize: '0.85rem' }}>Starting from</Text>
            <Title level={3} className="price-text" style={{ margin: '4px 0', color: '#FF9A76' }}>
              €{priceRange.min.toFixed(2)}
            </Title>
            {priceRange.min !== priceRange.max && (
              <Text type="secondary" style={{ fontSize: '0.85rem' }}>
                Up to €{priceRange.max.toFixed(2)}
              </Text>
            )}
          </div>
        ) : (
          <Text type="secondary">Price unavailable</Text>
        )}

        {/* Partner info */}
        {product.partner_name && (
          <div className="product-partner-info">
            <div className="partner-name-row">
              <ShopOutlined />
              <Text
                className="partner-name partner-link"
                ellipsis
                onClick={(e) => {
                  e.stopPropagation();
                  if (product.partner_id) {
                    navigate(`/partners/${product.partner_id}`);
                  }
                }}
                style={{ cursor: product.partner_id ? 'pointer' : 'default' }}
              >
                {product.partner_name}
              </Text>
              {product.partner_segment === 'FOUNDING' && (
                <FoundingPartnerBadge size="small" />
              )}
              {product.partner_is_impact && (
                <Tooltip title={t('products.impactPartner')}>
                  <span style={{ marginLeft: 4 }}>🤝</span>
                </Tooltip>
              )}
              {product.partner_count > 1 && (
                <Tooltip title={t('products.multipleSellers', { count: product.partner_count })}>
                  <Tag size="small" color="blue">
                    +{product.partner_count - 1} {t('products.seller', { count: product.partner_count - 1 })}
                  </Tag>
                </Tooltip>
              )}
            </div>
            {product.partner_city && (
              <div className="partner-location">
                <EnvironmentOutlined />
                <Text type="secondary">{getCityByLanguage(product.partner_city, i18n.language)}</Text>
              </div>
            )}
          </div>
        )}

        <Space className="product-meta" size="small">
          {variantCount > 0 && (
            <Text type="secondary" className="variant-count">
              <ShoppingCartOutlined /> {variantCount} {variantCount === 1 ? 'option' : 'options'}
            </Text>
          )}
        </Space>
      </div>
    </Card>
  );
};

export default ProductCard;
